import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { AuthService, BreadcrumbService, CompanyService, HomeService, PanoramaService, StateService, Utils } from 'src/app/services';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from 'src/app/services/config.service';
import { IFloorplanSettings } from 'src/app/shared/models/floorplan-settings.interface';
import { EnvironmentName, RenderType, GetMostPopularRenderedHomeByBuilderRequest, IfcFileArtifact, ListHomesByCompanyRequest, ListHomesByCompanyResponse, Home,  } from 'situview-webapi-client'
import { Company, ICompany } from 'identity-webapi-client';
import { GetMostPopularRenderedHomesByBuilderResponse, GetRendererNamesRequest, SituAnalyticsWebApiClient } from 'situanalytics-webapi-client';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
  ArtifactEnvironment, GetHomesByBuilderRequest, GetMasterFilesByHomeRequest,
  GetOptionsByHomeRequest, GetThemesByBuilderRequest
} from 'situ-warpapi-client';
import { trigger, transition, animate, keyframes } from '@angular/animations';
import * as kf from '../../../core/keyframes';
import { 
  AddPriorityConfigRequest, AddPriorityConfigResponse, PriorityConfig, ProductName, SituRenderServiceWebApiClient
} from 'siturenderservice-webapi-client';
import { Role } from 'src/app/shared/models/role.model';

@Component({
  selector: 'home-viewer',
  templateUrl: './home-viewer.component.html',
  styleUrls: ['./home-viewer.component.less'],
  animations: [
    trigger('cardAnimator', [
      transition('* => headShake', animate(1000, keyframes(kf.headShake)))
    ])
  ]
})
export class HomeViewerComponent implements OnInit, OnDestroy {
  onLoadHome = new Subject<any>();
  onLoadConfigHome = new Subject<any>();
  pluginManagerLoaded = new ReplaySubject<any>();

  PluginManager: any;
  SituSystems: any;

  isDarkMode: boolean = false;
  company: ICompany | undefined;
  
  selectedHomeConfig: IHomeConfigShortcut = new HomeConfigShortcut();
  hasCheckedParams = false;
  isSituUser = false;
  isAdmin = false;
  isFurnishUser = false;

  selectedRenderer: string = '';
  renderers: string[] = [];

  homes: any[] = [];
  masterFiles: string[] = [];
  options: SelectableOption[] = [];
  themes: SelectableTheme[] = [];

  showFloorPlan = false;
  floorPlanLocked = false;
  roomListVisible = false;
  roomListLocked = false;

  // Loading Spinner
  loadingSplash: string | undefined;
  loadingValue: string | undefined;

  // Misc settings
  error: string | undefined;
  deviceHasGyro: boolean = false;
  hideControls: boolean = false;
  interactiveFloorPlan = true;
  gyroTooltip: string | undefined;
  selectedIndex: any;
  showFullscreenControls = false;
  openAngle: number | null = null;
  closeAngleDifference = 15;
  disableControls: boolean = false;
  noHomes: boolean = false;
  firstRoomLoaded: boolean = false;
  animateAddHome: string = '';
  artifactId: string | undefined;

  currentParams: any;

  shortCuts: IHomeConfigShortcut[] = [];
  shortCutsHistory: IHomeConfigShortcut[] = [];

  outputs: string[] = [];
  floors = [true, false, false];

  showForceRefresh = false;
  disableForceRefresh = false;

  floorplanSettings: IFloorplanSettings | undefined;
  floorplanInteractive = true;

  _smallFloorplanSettings = {
    planScalingFactor: 15,
    roomNameFontSize: '10'
  } as IFloorplanSettings;

  _largeFloorplanSettings = {
    planScalingFactor: 20,
    roomNameFontSize: '14'
  } as IFloorplanSettings;

  floorPlansAvailable: string[] = [];
  floorPlanInteractive = true;

  public get smallFloorplanSettings() {
    return { ...this.floorplanSettings, ...this._smallFloorplanSettings };
  }
  public get largeFloorplanSettings() {
    return { ...this.floorplanSettings, ...this._largeFloorplanSettings };
  }

  public get viewingAngleForDisplay() {
    return typeof this.selectedHomeConfig?.viewingAngle !== "number" || isNaN(this.selectedHomeConfig?.viewingAngle) ? 0 : this.selectedHomeConfig?.viewingAngle;
  }

  private _showLoading = false;
  public get showLoading(): boolean {
    return this._showLoading;
  }

  public set showLoading(val: boolean) {
    this._showLoading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  _panoramaOverlay: string = "panorama-overlay";

  static readonlyType: string = "view";

  private _subscriptions: Subscription[] = [];
  private _stateSubscription: Subscription | undefined;

  constructor(private route: ActivatedRoute,
    private panoramaService: PanoramaService,
    private authService: AuthService,
    private router: Router,
    private clipboard: Clipboard,
    private _snackBar: MatSnackBar,
    private configService: ConfigService,
    private breadcrumbService: BreadcrumbService,
    private stateService: StateService,
    private homeService: HomeService,
    private companyService: CompanyService,
    private renderService: SituRenderServiceWebApiClient,
    private situAnalyticsClient: SituAnalyticsWebApiClient) {
  }

  ngOnInit(): void {
    this.adjustFloorPlanScale(window.innerWidth);
    this._subscriptions.push(this.panoramaService.onSituViewLoad.subscribe(() => {
      this.buildAndRunPluginManager()
      this.sendConsoleUpdate("init");

      this._stateSubscription = this.stateService.state$.subscribe(state => {
        this.isDarkMode = state.useDarkTheme;

        if ((state.company?.code ?? "") !== this.company?.code) {
          this.authService.userSubject.subscribe(() => {
            this.company = state.company ?? undefined;

            // query param overrides 
            let builderParam = this.route.snapshot.queryParamMap.get('builder')?.toLowerCase();
            if (!this.hasCheckedParams && builderParam && builderParam != this.company?.code?.toLowerCase() && this.authService.isAuthorizedForCompanyByCode(builderParam)) {
              this.hasCheckedParams = true;
              this.companyService.getCompanyByCode(builderParam).then(x => {
                let company = x as Company;
                if (company && this.stateService.state.company?.code?.toLowerCase() != company.code?.toLowerCase()) {

                  this.stateService.state = {
                    ...this.stateService.state,
                    company
                  };

                  return;
                }
                else {
                  this.alertError("Could not find company");
                }
              });
              return;
            }
            this.hasCheckedParams = true;

            this.reset();

            this.shortCutsHistory = state.homeHistory?.filter(x => x.builder.toLowerCase() === state.company?.code?.toLowerCase()) ?? [];

            if (builderParam?.toLowerCase() === state.company?.code?.toLowerCase()) {
              let home = this.route.snapshot.queryParamMap.get('home')?.toLowerCase();
              if (home) {
                let env = parseInt(this.route.snapshot.queryParamMap.get("env") ?? '') ?? EnvironmentName.Production;
                let render = parseInt(this.route.snapshot.queryParamMap.get("render") ?? '') ?? RenderType.BestAvailable;
                let themes = this.route.snapshot.queryParamMap.get('themes')?.toLowerCase();
                let options = this.route.snapshot.queryParamMap.get('options')?.toLowerCase();
                let masterFile = this.route.snapshot.queryParamMap.get("masterFile")?.toLowerCase() ?? "";
                let room = this.route.snapshot.queryParamMap.get('room')?.toLowerCase();
                let viewingAngle = parseInt(this.route.snapshot.queryParamMap.get('angle')?.toLowerCase() ?? '') ?? 0;

                this.selectHomeConfig(home, masterFile ?? '', themes ? themes.split(",") : [], options ? options.split(",") : [], render, env, room, viewingAngle, false, builderParam);
              }
            }
            this.loadMostPopularHomes();
          });
        }
      });
    }));

    this.isSituUser = this.authService.hasSituAccess();
    this.isAdmin = this.authService.hasAnyRole([Role.Admin, Role.BuilderAdmin]);
    this.isFurnishUser = this.authService.hasSituAccess() || this.authService.hasRole(Role.SituFurnish);
    this.authService.userSubject.subscribe(() => {
      this.isSituUser = this.authService.hasSituAccess();
      if (this.PluginManager) {
        this.PluginManager.authorizationHeaderValue = this.authService.getAuthorizationHeaderValue();
      }
    });

    this.situAnalyticsClient.getRendererNames(new GetRendererNamesRequest())
      .then(response => {
        this.renderers = response.rendererNames?.filter(x => x.toLowerCase().indexOf('-vanity') == -1 && x.toLowerCase().indexOf('-ifc') == -1) ?? [];
      });

    // Gyroscope controls
    this.deviceHasGyro = false;
    this.gyroTooltip = "Gyroscope is not compatible with this device.";

    window.addEventListener("devicemotion", (event) => {
      if (event?.rotationRate?.alpha || event?.rotationRate?.beta || event?.rotationRate?.gamma) {
        if (!this.deviceHasGyro) {
          this.gyroTooltip = "Enable/Disable Gyroscope Controls";
          this.deviceHasGyro = true;
        }
      }
    });

    window.addEventListener('resize', () => {
      this.adjustFloorPlanScale(window.innerWidth);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    if (this._stateSubscription) {
      this._stateSubscription.unsubscribe();
    }
  };

  private unsubscribe() {
    if (this._subscriptions) {
      for (let sub of this._subscriptions) {
        sub.unsubscribe();
      }
      this._subscriptions = [];
    }
    this.PluginManager.removeEventListener("homeDetails", this.homeDetailsHandler);
    //new debugging fields
    this.PluginManager.removeEventListener("panoramaselected", this.panoramaSelectedHandler);
    this.PluginManager.removeEventListener("productcalloutshown", this.productCalloutShownHandler);
    this.PluginManager.removeEventListener("productcallouthidden", this.productCalloutHiddenHandler);
  };

  private selectHomeConfig(home: string, masterFile: string, themes: string[], options: string[], renderType: RenderType, environmentName: EnvironmentName, room: string = "kitchen", viewingAngle: number = 0, userRequested: boolean = false, builder: string = "") {
    this.selectedHomeConfig = {
      builder: this.company?.code ? this.company?.code : builder,
      home: home,
      masterFile: masterFile,
      themes: themes,
      options: options,
      render: renderType,
      room: room,
      viewingAngle: viewingAngle,
      environment: environmentName,
      historic: userRequested
    } as IHomeConfigShortcut;

    this.loadData().then(() => {
      this.loadHome(userRequested);
    });
  }

  private reset() {
    this.showLoading = true;
    this.disableControls = false;
    this.firstRoomLoaded = false;
    this.hideControls = false;
    this.noHomes = false;
    this.shortCuts = [];
    this.shortCutsHistory = [];
    this.themes = [];
    this.options = [];
    this.homes = [];
    this.masterFiles = [];
    this.error = undefined;
    this.selectedHomeConfig = new HomeConfigShortcut();
  }

  private loadMostPopularHomes() {
    let request = new GetMostPopularRenderedHomeByBuilderRequest();
    request.ownerId = this.company?.id;
    request.pageNumber = 1;
    request.pageSize = 5;
    request.daysPreviousToSearch = 1;

    this.homeService.GetMostPopularHomesByBuilder(request)
      .then((response: GetMostPopularRenderedHomesByBuilderResponse) => {
        if (response != null && response.success) {
          if (response?.logItems) {
            this.shortCuts = response
              .logItems
              .slice(0, 3)
              .map((x: any) => {
                return {
                  builder: x.builder?.toLowerCase(),
                  home: x.homeName?.toLowerCase(),
                  masterFile: x.facade?.toLowerCase(),
                  themes: x.themes ? x.themes?.toLowerCase().split("~") : [],
                  options: x.options ? this.getOptions(x.options?.toLowerCase()) : [],
                  render: RenderType.BestAvailable,
                  room: "kitchen",
                  viewingAngle: 0,
                  historic: false,
                  environment: EnvironmentName.Production
                } as IHomeConfigShortcut;
              }) ?? [];
          }

          let mostPopularHome = this.shortCuts?.find(e => typeof e !== 'undefined');
          if (mostPopularHome && !this.selectedHomeConfig?.home) {
            this.selectHomeConfig(mostPopularHome.home, mostPopularHome.masterFile, mostPopularHome.themes, mostPopularHome.options, mostPopularHome.render, mostPopularHome.environment);
          } else if (!this.selectedHomeConfig?.home) {
            this.loadData().then(() => {
              if (!this.selectedHomeConfig.home) {
                this.setHomeAsBridgeHome();
              }
              else {
                this.loadHome(false);
              }
            });
          }
        }
      })
      .catch((err: any) => {
        this._snackBar.open("Error loading home!");
      });
  }

  private loadData() {
    return Promise.all([this.getHomes(), this.getThemesByBuilder()])
      .then(() => {
        return Promise.all([this.getMasterFilesByHome(), this.getOptionsByHome()])
      });
  }

  onSelectedHomeIfcFileId(homeIfcFileId: string) {
    this.router.navigate([`/homes/furnish/${homeIfcFileId}`]);
  }

  onClickAddHome() {
    this.router.navigate([`/homes/upload`]);
    return;
  };

  setHomeAsBridgeHome() {
    this.animateAddHome = "headShake";
    this.noHomes = true;
    this.showLoading = false;
    this.disableControls = false;
  };
 
  //listener outputs
  viewingAngle: number | undefined;
  verticalViewingAngle: number | undefined;
  selectedPanorama: string | undefined;
  selectedProductManufacturer: string | undefined;
  selectedProductSku: string | undefined;
  copyDebugUrl() {
    const baseUrl = this.configService.config.situLinkBaseUrl 
      ? `https://view.${this.configService.config.situLinkBaseUrl}`
      : this.configService.config.situPortalBaseUrl;
  
    const url = new URL(baseUrl);
    url.pathname = this.artifactId!;
  
    if (this.selectedPanorama) {
      url.searchParams.set('panorama', this.selectedPanorama);
  
      if (this.viewingAngle) {
        url.searchParams.set('hlookat', this.viewingAngle.toString());
      }
  
      if (this.verticalViewingAngle) {
        url.searchParams.set('vlookat', this.verticalViewingAngle.toString());
      }
  
      if (this.selectedProductManufacturer) {
        url.searchParams.set('manufacturer', this.selectedProductManufacturer);
      }
  
      if (this.selectedProductSku) {
        url.searchParams.set('sku', this.selectedProductSku);
      }
    }
  
    this.clipboard.copy(url.toString());
    this.showMessage("Copied share location to clipboard!");
  }

  loadHome(userRequested: boolean) {
    if (userRequested) {
      this.setHomeConfigHistory({
        builder: this.company?.code,
        home: this.selectedHomeConfig?.home,
        masterFile: this.selectedHomeConfig?.masterFile,
        themes: this.selectedHomeConfig?.themes,
        options: this.selectedHomeConfig?.options,
        render: this.selectedHomeConfig?.render,
        room: this.selectedHomeConfig?.room ?? "kitchen",
        viewingAngle: this.selectedHomeConfig?.viewingAngle ?? 0,
        historic: userRequested,
        environment: this.selectedHomeConfig?.environment
      } as IHomeConfigShortcut);
    }
    let homeConfig = { builder: this.company?.code, home: this.selectedHomeConfig?.home, masterFile: this.selectedHomeConfig?.masterFile, themes: this.selectedHomeConfig?.themes, options: this.selectedHomeConfig?.options };
    let startingRoom = this.selectedHomeConfig?.room ?? "kitchen";
    let artifactEnvironment = { company: this.company?.code, environment: this.selectedHomeConfig?.environment };
    let roomSettings: any[] = [];
    let data = {
      homeConfig: homeConfig, startingRoom: startingRoom, environment: this.selectedHomeConfig?.environment, artifactEnvironment: artifactEnvironment,
      roomSettings: roomSettings, renderType: this.selectedHomeConfig?.render, forceRefresh: false, renderer: this.selectedRenderer
    };
    this.artifactId = undefined;
    this.floorPlanLocked = true;
    this.showFloorPlan = true;
    this.firstRoomLoaded = false;
    this.disableControls = false;
    this.floorPlansAvailable = [];
    this.hideRoomList();
    this.currentParams = data;
    this.showLoading = true;
    this.loadingSplash = undefined;
    this.loadingValue = undefined;
    this.prepareHomeForLoad(data);
  };

  queueHome() {
    if (this.selectedHomeConfig) {

      let renderType = this.getRenderTypeForPriorityList(this.selectedHomeConfig.render);
      let productName = this.renderTypeToProductName(renderType);

      let request = new AddPriorityConfigRequest();
      request.companyId = this.company?.id;
      request.productName = productName;
      let priorityConfig = new PriorityConfig();
      priorityConfig.createRequestBodyJson = this.generateHomeJson();
      priorityConfig.situApiCreateRequestBodyJson = this.generateHomeJson();
      request.priorityConfig = priorityConfig;

      this.renderService.addPriorityConfig(request)
        .then((response: AddPriorityConfigResponse) => {
          if (response.success) {
            this._snackBar.open("Success! This home config has been added to your priority home queue.");
          } else {
            this._snackBar.open("Sorry! There was an issue at our end adding this home config to the priority render queue. Please try again.");
          }
        })
        .catch((err: any) => {
          this._snackBar.open("Sorry! There was an issue at our end adding this home config to the priority render queue. Please try again.");
        });
    }
  };

  private prepareHomeForLoad(data: any) {
    // Setting the resolution of the panorama viewer.
    this.error = undefined;

    // this.onLoadConfigHome.next(data);
    this.onLoadHome.next(data);
    this.updateURLParams();

    if (!this.PluginManager.isFullscreen) { this.selectedIndex = 0; }
  };

  loadShortCuts(homeConfigShortcut: IHomeConfigShortcut) {
    this.selectHomeConfig(homeConfigShortcut.home, homeConfigShortcut.masterFile, homeConfigShortcut.themes, homeConfigShortcut.options, homeConfigShortcut.render ?? RenderType.BestAvailable, homeConfigShortcut.environment ?? EnvironmentName.Production, homeConfigShortcut.room, homeConfigShortcut.viewingAngle);
  };

  updateLoadSpinner(detail: any) {
    this.loadingSplash = detail.description;
    this.loadingValue = detail.percentComplete.toString();

    if (detail.percentComplete === 100 || detail.percentComplete === -1) {
      this.showLoading = false;
    } else {
      this.showLoading = true;
    }
  };

  toggleFullscreen() {
    this.PluginManager.toggleFullScreen();
  };

  toggleGyro() {
    this.PluginManager.toggleGyro();
  };

  setHideControls(value: any) {
    this.hideControls = value;
  };

  toggleFullScreenPanel() {
    this.showFullscreenControls = !this.showFullscreenControls;
  };

  updateURLParams() {
    if (this.PluginManager == null) {
      return;
    }

    var query = { builder: this.company?.code, home: this.selectedHomeConfig?.home, masterFile: this.selectedHomeConfig?.masterFile, room: this.selectedHomeConfig?.room, env: this.selectedHomeConfig?.environment, render: this.selectedHomeConfig?.render } as any;

    let options = this.selectedHomeConfig?.options.join(",");
    if (options) {
      query.options = options;
    }

    let themes = this.selectedHomeConfig?.themes.join(",");
    if (themes) {
      query.themes = themes;
    }

    this.router.navigate(['.'], { relativeTo: this.route, queryParams: query });
  };

  urlToClipboard() {
    let url = `${this.configService.config.situPortalBaseUrl}${this.router.url}&angle=${this.selectedHomeConfig?.viewingAngle}`;
    this.clipboard.copy(url);
    this._snackBar.open(`Copied config to clipboard!`, "Close", {
      duration: 5000,
      panelClass: ['copied-snackbar']
    });
  };

  shareHome() {
    if (this.artifactId) {
      let url = this.configService.config.situLinkBaseUrl ?
         `https://${HomeViewerComponent.readonlyType}.${this.configService.config.situLinkBaseUrl}/${this.artifactId}` :
          `${this.configService.config.situPortalBaseUrl}/${this.artifactId}`;
      this.clipboard.copy(url);
      this.showMessage(`Copied config to clipboard!`);
    }
  };

  toggleFloorPlan() {
    if (this.showFloorPlan) {
      this.hideFloorPlans();
    } else {
      this.advancedVisible = false;
      this.showFloorPlan = true;
      this.openAngle = this.selectedHomeConfig?.viewingAngle ?? null;
      this.hideRoomList();
      this.advancedVisible = false;
    }
  };

  toggleFloorLock() {
    this.floorPlanLocked = !this.floorPlanLocked;
  };

  toggleRoomList() {
    if (this.roomListVisible) {
      this.hideRoomList();
    } else {
      this.advancedVisible = false;
      this.openAngle = this.selectedHomeConfig?.viewingAngle ?? null;
      this.hideFloorPlans();
      this.roomListVisible = true;
    }
  };

  toggleRoomLock() {
    this.roomListLocked = !this.roomListLocked;
  };

  hideFloorPlans() {
    this.showFloorPlan = false;
    this.floorPlanLocked = false;
  };

  hideRoomList() {
    this.roomListVisible = false;
    this.roomListLocked = false;
  };

  hideMenus() {
    this.hideFloorPlans();
    this.hideRoomList();
    this.openAngle = null;
    this.openAngle = null;
    this.advancedVisible = false;
  };

  switchFloorPlan(id: any) {
    this.floors = [false, false, false];
    this.floors[id] = true;
  };

  advancedVisible = false;
  advancedEnabled = false;
  toggleAdvancedOptions() {
    if (this.advancedVisible) {
      this.advancedVisible = false;
      return;
    }

    this.hideMenus();
    this.advancedVisible = true;
  };

  getTooltip(shortCut: IHomeConfigShortcut): string {
    let tooltip = `MasterFile: ${shortCut.masterFile}`;
    if (shortCut.options && shortCut.options?.length > 0 && shortCut.options[0]) {
      tooltip = tooltip + ` - Options: ${shortCut.options}`
    }
    if (shortCut.themes && shortCut.themes.length > 0 && shortCut.themes[0]) {
      tooltip = tooltip + ` - Themes: ${shortCut.themes}`
    }
    return tooltip;
  };

  selectEnvironment(environment: number) {
    if (!this.selectedHomeConfig) {
      this.selectedHomeConfig = new HomeConfigShortcut();
    }
    this.selectedHomeConfig.environment = environment;

    this.loadData();
  };

  selectRenderer(renderer: string) {
    this.selectedRenderer = renderer;
  }

  selectRenderType(renderType: number) {
    if (!this.selectedHomeConfig) {
      this.selectedHomeConfig = new HomeConfigShortcut();
    }
    this.selectedHomeConfig.render = renderType;
  };

  setViewAngle(viewingAngle: number) {
    if (!this.selectedHomeConfig) {
      this.selectedHomeConfig = new HomeConfigShortcut();
    }
    this.selectedHomeConfig.viewingAngle = viewingAngle;
  };

  private setHomeConfigHistory(configShortcut: IHomeConfigShortcut) {
    let homeHistory = this.stateService?.state?.homeHistory ?? [];
    let exists = false;
    if (homeHistory.length > 0) {
      for (let i = 0; i < homeHistory.length; i++) {
        let x = homeHistory[i];
        if (!this.hasHomeConfigChanged(x, configShortcut)) {
          exists = true;
          break;
        }
      }
    }
    if (!exists) {
      homeHistory = homeHistory
        .reverse()
        .slice(0, 2)
        .reverse();

      configShortcut.historic = true;
      homeHistory.push(configShortcut);

      this.stateService.state = {
        ...this.stateService.state,
        homeHistory
      };
    }
  };

  selectHome(home: string) {
    if (!this.selectedHomeConfig) {
      this.selectedHomeConfig = new HomeConfigShortcut();
    }

    if (this.selectedHomeConfig.home == home) {
      return;
    }

    this.selectedHomeConfig.home = home;
    this.selectedHomeConfig.masterFile = "";
    this.selectedHomeConfig.options = [];
    Promise.all([this.getOptionsByHome(), this.getMasterFilesByHome()]);
  };

  private getHomes(): Promise<void> {
    let request = new GetHomesByBuilderRequest();
    request.builder = this.company?.code;

    let environment = new ArtifactEnvironment();
    environment.company = this.company?.code;
    environment.environment = this.selectedHomeConfig.environment;

    request.environment = environment;
    return this.homeService.GetHomesByBuilder(request)
      .then(response => {
        this.homes = response.homes ?? [];

        if (!this.homes.find(x => x.toLowerCase() == this.selectedHomeConfig.home.toLowerCase())) {
          this.selectedHomeConfig.home = '';
        }

        // set a default if none are selected
        if (!this.selectedHomeConfig.home.length && this.homes.length) {
          this.selectedHomeConfig.home = this.homes[0];
        }
      })
      .catch(err => {
        this._snackBar.open("Failed to load homes list!");
      });
  };

  selectMasterFile(masterFile: string) {
    if (this.selectedHomeConfig) {
      this.selectedHomeConfig.masterFile = masterFile;
    }
  };

  private getMasterFilesByHome() {
    if (!this.selectedHomeConfig.home) {
      this.masterFiles = [];
      return;
    }

    let request = new GetMasterFilesByHomeRequest();
    request.builder = this.company?.code;
    request.home = this.selectedHomeConfig.home;
    let environment = new ArtifactEnvironment();
    environment.company = this.company?.code;
    environment.environment = EnvironmentName.Latest;
    environment.environment = this.selectedHomeConfig.environment;
    request.environment = environment;

    return this.homeService.GetMasterFilesByHome(request)
      .then(response => {
        this.masterFiles = response.masterFiles ?? [];

        if (this.selectedHomeConfig.masterFile && !this.masterFiles.find(x => x.toLowerCase() == this.selectedHomeConfig.masterFile.toLowerCase())) {
          this.selectedHomeConfig.masterFile = '';
        }

        // set a default master file if none are selected
        if (!this.selectedHomeConfig.masterFile.length && this.masterFiles.length) {
          this.selectedHomeConfig.masterFile = this.masterFiles[0];
        }
      })
      .catch(err => { this._snackBar.open("Failed to load master files!") });
  };

  toggleTheme(theme: SelectableTheme) {
    if (!this.selectedHomeConfig) {
      this.selectedHomeConfig = new HomeConfigShortcut();
    }

    if (theme.selected && this.selectedHomeConfig) {
      this.selectedHomeConfig.themes = this.selectedHomeConfig.themes.filter(obj => obj !== theme.name);
    }

    if (!theme.selected && this.selectedHomeConfig) {
      this.selectedHomeConfig.themes.push(theme.name)
    }

    theme.selected = !theme.selected;
  };

  private getThemesByBuilder(): Promise<void> {
    if (!this.company) {
      this.themes = [];
      return Promise.resolve();
    }

    let request = new GetThemesByBuilderRequest();
    request.builder = this.company?.code;
    let environment = new ArtifactEnvironment();
    environment.company = this.company?.code;
    environment.environment = this.selectedHomeConfig.environment;
    request.environment = environment;

    return this.homeService.GetThemesByBuilder(request)
      .then(response => {
        let themes = response.themes?.map(x => new SelectableTheme(x, ThemeType.Theme, this.selectedHomeConfig?.themes?.indexOf(x) != -1)) ?? [];
        let decoratorThemes = response.decoratorThemes?.map(x => new SelectableTheme(x, ThemeType.Decorator, this.selectedHomeConfig?.themes?.indexOf(x) != -1)) ?? [];
        let furnishingThemes = response.furnishingThemes?.map(x => new SelectableTheme(x, ThemeType.Furnishing, this.selectedHomeConfig?.themes?.indexOf(x) != -1)) ?? [];
        let furnishingPlacementSchemes = response.furnishingPlacementSchemes?.map(x => new SelectableTheme(x, ThemeType.FurnishingPlacementScheme, this.selectedHomeConfig?.themes?.indexOf(x) != -1)) ?? [];
        let productReplacementThemes = response.productReplacementThemes?.map(x => new SelectableTheme(x, ThemeType.ProductReplacement, this.selectedHomeConfig?.themes?.indexOf(x) != -1)) ?? [];
        let parameterThemes = response.parameterThemes?.map(x => new SelectableTheme(x, ThemeType.ParameterTheme, this.selectedHomeConfig?.themes?.indexOf(x) != -1)) ?? [];

        this.themes = [...themes, ...decoratorThemes, ...furnishingThemes, ...furnishingPlacementSchemes, ...productReplacementThemes, ...parameterThemes].sort((a, b) => (a.name < b.name ? -1 : 1));

        // remove selected themes that are not available
        this.selectedHomeConfig.themes = this.selectedHomeConfig?.themes.filter(x => !!this.themes.find(y => y.name.toLowerCase() == x.toLowerCase())) ?? [];

        // set a default theme if none are selected
        if (!this.selectedHomeConfig.themes.length && this.themes.length) {
          let defaultTheme = this.themes.find(x => x.type == ThemeType.Decorator);
          if (defaultTheme) {
            defaultTheme.selected = true;
            this.selectedHomeConfig.themes.push(defaultTheme.name);
          }
        }
      });
  };

  getTheme(themeName: string) {
    return this.themes.find(x => x.name == themeName);
  }

  getThemeColor(theme: SelectableTheme, isText: boolean) {
    if (!theme) {
      return;
    }

    switch (theme.type) {
      case ThemeType.Theme:
        return isText ? "" : "basic";
      case ThemeType.Decorator:
        return isText ? "text-primary" : "primary";
      case ThemeType.Furnishing:
        return isText ? "text-success" : "accent";
      case ThemeType.FurnishingPlacementScheme:
        return isText ? "text-warning" : "warn";
      case ThemeType.ProductReplacement:
        return isText ? "" : "basic";
      case ThemeType.ParameterTheme:
        return isText ? "text-info" : "basic";
    }

    return null;
  };

  clearThemes() {
    this.themes.forEach(x => x.selected = false);
    if (this.selectedHomeConfig) {
      this.selectedHomeConfig.themes = [];
    }
  };

  toggleOption(event: MatSlideToggleChange, ifc: IfcFileArtifact) {
    if (event.checked && ifc.name && this.selectedHomeConfig) {
      this.selectedHomeConfig.options.push(ifc.name)
    }

    if (!event.checked && this.selectedHomeConfig) {
      this.selectedHomeConfig.options = this.selectedHomeConfig.options.filter(currentlySelected =>
        currentlySelected.toLowerCase() !== ifc.name?.toLowerCase() &&
        currentlySelected.toLowerCase() !== ifc.name?.toLowerCase()?.replace(" gf", "") &&
        currentlySelected.toLowerCase() !== ifc.name?.toLowerCase()?.replace(" ff", "")
      );
    }
  };

  private getOptions(optionsText: string): string[] {
    let options = optionsText.split(" ");
    let result = new Array<string>();
    options.forEach((x, index) => {
      if (x) {
        let optionSanitized = x.toLowerCase();
        if (optionSanitized !== "ff" && optionSanitized !== "gf") {
          if (options[index + 1]) {
            let floorOption = options[index + 1].toLowerCase();
            if (floorOption === "ff" || floorOption === "gf") {
              result.push(`${x} ${options[index + 1]}`);
            } else {
              result.push(x);
            }
          } else {
            result.push(x);
          }
        }
      }
    })
    return result;
  }

  private getOptionsByHome() {
    if (!this.selectedHomeConfig.home) {
      this.options = [];
      return;
    }

    let request = new GetOptionsByHomeRequest();
    request.builder = this.company?.code;
    request.home = this.selectedHomeConfig.home;

    let environment = new ArtifactEnvironment();
    environment.company = this.company?.code;
    environment.environment = this.selectedHomeConfig.environment;
    request.environment = environment;

    return this.homeService.GetOptionsByHome(request)
      .then(response => {
        this.options = response.options?.map(x => new SelectableOption(x, !!this.selectedHomeConfig.options?.find(y => x.toLowerCase().startsWith(y.toLowerCase())))) ?? [];

        // remove selected options that are not available
        this.selectedHomeConfig.options = this.selectedHomeConfig.options?.filter(x => !!this.options.find(y => y.name.toLowerCase().startsWith(x.toLowerCase()))) ?? [];
      })
      .catch(err => { this._snackBar.open("Failed to load options!") });
  };

  private buildAndRunPluginManager() {
    // Load a default home.
    this.SituSystems = this.panoramaService.situSystems;
    this.floorplanSettings = new this.SituSystems.FloorplanSettings();
    // Create a new plugin manager from the service.
    this.PluginManager = this.panoramaService.createPluginManager();
    this.pluginManagerLoaded.next(this.PluginManager);

    this.PluginManager.addEventListener("pluginadded", (e: CustomEvent) => {
      this.sendConsoleUpdate("pluginadded " + e.detail.plugin.name);
    });

    this.PluginManager.addEventListener("roomselected", (e: CustomEvent) => {
      if (this.selectedHomeConfig) this.selectedHomeConfig.room = e.detail.roomName;
      this.updateURLParams();
      this.sendConsoleUpdate("Room selected: " + e.detail.roomName);
    });

    this.PluginManager.addEventListener("floorplanavailable", (e: CustomEvent) => {
      this.disableControls = false;
      if (!this.firstRoomLoaded) this.firstRoomLoaded = true;
      this.sendConsoleUpdate("floorplanavailable " + JSON.stringify(e.detail));
      this.floorPlansAvailable = [...this.floorPlansAvailable, e.detail.floorPlanName];
      if (this.floorPlansAvailable.length == 1) {
        this.showFloorPlan = true;
      }
    });

    this.PluginManager.addEventListener("roompanoramaavailable", (e: CustomEvent) => {
      this.disableControls = false;
      this.sendConsoleUpdate("roompanoramaavailable " + JSON.stringify(e.detail));
    });

    this.PluginManager.addEventListener("homeconfigloadprogress", (e: CustomEvent) => {
      this.sendConsoleUpdate("homeconfigloadprogress " + JSON.stringify(e.detail));
      this.updateLoadSpinner(e.detail);

      if (e?.detail?.description && e.detail.description === "Error loading home" &&
        e?.detail?.percentComplete && e.detail.percentComplete === -1) {
        this.alertError(e.detail.description);
      }
    });

    this.PluginManager.addEventListener("homeDetails", this.homeDetailsHandler);

    this.PluginManager.addEventListener("homereset", (e: CustomEvent) => {
      this.floorPlansAvailable = [];
      this.showFloorPlan = false;
      this.sendConsoleUpdate("homereset");
    });

    this.PluginManager.addEventListener("homeloaderror", (e: CustomEvent) => {
      this.alertError(e.detail);
      this.sendConsoleUpdate("homeloaderror " + JSON.stringify(e.detail));
    });

    this.PluginManager.addEventListener("unhandlederror", (e: CustomEvent) => {
      this.alertError(e.detail);
      this.sendConsoleUpdate("unhandlederror " + JSON.stringify(e.detail));
    });

    this.PluginManager.addEventListener("viewinganglechange", (e: CustomEvent) => {
      if (e.detail.viewingAngle && this.openAngle == null) {
        this.openAngle = this.selectedHomeConfig?.viewingAngle ?? null;;
      }

      if (e?.detail?.viewingAngle) {
        this.viewingAngle = e.detail.viewingAngle;
        this.verticalViewingAngle = e.detail.verticalViewingAngle;
      }

      if (e.detail.viewingAngle && this.openAngle != null) {
        if (!this.roomListVisible && !this.showFloorPlan && (this.floorPlanLocked || this.roomListLocked)) {
          return;
        }

        if ((e.detail.viewingAngle - this.openAngle) > this.closeAngleDifference || (this.openAngle - e.detail.viewingAngle) > this.closeAngleDifference) {
          if (!this.roomListLocked) {
            this.hideRoomList();
          }
          if (!this.floorPlanLocked) {
            this.hideFloorPlans();
          }
        }
      }
    });

    //new debugging fields
    this.PluginManager.addEventListener("panoramaselected", this.panoramaSelectedHandler);
    this.PluginManager.addEventListener("productcalloutshown", this.productCalloutShownHandler);
    this.PluginManager.addEventListener("productcallouthidden", this.productCalloutHiddenHandler);
  };

  private homeDetailsHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.artifactId) {
      this.artifactId = e.detail.artifactId;
    }
  };

  alertError(event: any) {
    this.disableControls = false;
    this.showLoading = false;
    this.error = typeof event === 'string' ? event : "Error loading home";
  };

  private sendConsoleUpdate(message: string) {
    var element = document.getElementById('consoleScroll');
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
    const now = new Date();
    let dateString = `${now.getDay()}/${now.getMonth()}/${now.getFullYear()} ${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}.${now.getMilliseconds()}`;
    this.outputs.push("[" + dateString + "] " + message);
  };

  private adjustFloorPlanScale(windowWidth: number) {
    let largePlanScalingFactor = 20;
    let largeRoomNameFontSize = 12;
    let planScalingFactor = 12;
    let roomNameFontSize = 8;

    if (windowWidth > 767) {
      largePlanScalingFactor = 30;
      largeRoomNameFontSize = 18;
      planScalingFactor = 16;
      roomNameFontSize = 10;
    }

    if (windowWidth > 1399) {
      planScalingFactor = 20;
      roomNameFontSize = 12;
    }

    if (windowWidth < 460) {
      largePlanScalingFactor = 16;
      largeRoomNameFontSize = 10;
    }

    if (windowWidth < 400) {
      largePlanScalingFactor = 12;
      largeRoomNameFontSize = 8;
    }

    this._largeFloorplanSettings.planScalingFactor = largePlanScalingFactor;
    this._largeFloorplanSettings.roomNameFontSize = largeRoomNameFontSize.toString();
    this._smallFloorplanSettings.planScalingFactor = planScalingFactor;
    this._smallFloorplanSettings.roomNameFontSize = roomNameFontSize.toString();
  };

  private hasHomeConfigChanged(to: IHomeConfigShortcut, from: IHomeConfigShortcut) {
    return to.home !== from.home ||
      to.masterFile !== from.masterFile ||
      to.options.join(",") !== from.options.join(",") ||
      to.themes.join(",") !== from.themes.join(",")
  }

  private getRenderTypeForPriorityList(selectedRenderType: RenderType) {
    switch (selectedRenderType) {
      case RenderType.Rasterized:
        return RenderType.Rasterized;
      case RenderType.VirtualReality:
        return RenderType.VirtualReality;
      case RenderType.Insights:
        return RenderType.Insights;
      case RenderType.PathTracedDraft:
        return RenderType.PathTracedDraft;
      case RenderType.PathTracedPlus:
        return RenderType.PathTracedPlus;
    }

    return RenderType.PathTraced;
  }
  private renderTypeToProductName(renderType: RenderType) {
    switch (renderType) {
      case RenderType.SituFacade:
        return ProductName.Facade;

      case RenderType.SituPlan:
        return ProductName.Plan;

      case RenderType.SituRender:
        return ProductName.Render;

      case RenderType.Vanity:
        return ProductName.Vanity;

      case RenderType.Insights:
        return ProductName.Insights;

      case RenderType.Rasterized:
        return ProductName.Panorama;

      case RenderType.VirtualReality:
        return ProductName.VR;

      case RenderType.PathTracedDraft:
        return ProductName.RayTracedDraft;

      case RenderType.PathTracedPlus:
        return ProductName.RayTracedPlus;

      default:
        return ProductName.RayTraced;
    }
  }

  private generateHomeJson() {
    let renderType = this.getRenderTypeForPriorityList(this.selectedHomeConfig.render);
    let productName = this.renderTypeToProductName(renderType);

    return JSON.stringify({
      "HomeConfig": {
        "Builder": this.selectedHomeConfig.builder,
        "Home": this.selectedHomeConfig.home,
        "MasterFile": this.selectedHomeConfig.masterFile,
        "Options": this.selectedHomeConfig.options,
        "Themes": this.selectedHomeConfig.themes
      },
      "ForceRefresh": false,
      "StartingRoom": null,
      "PreCache": true,
      "RenderType": renderType,
      "DateRequestedFor": null,
      "ArtifactEnvironment": {
        "Company": this.selectedHomeConfig.builder,
        "Environment": EnvironmentName.Staging,
        "Product": productName
      }
    });
  }

  private panoramaSelectedHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    //console.log(e);
    if (e?.detail?.panoramaName && this.selectedPanorama !== e.detail.panoramaName) {
      this.selectedPanorama = e.detail.panoramaName;
    }
  };

  private productCalloutShownHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.manufacturer && e?.detail?.sku) {
      this.selectedProductManufacturer = e.detail.manufacturer;
      this.selectedProductSku = e.detail.sku;
    }
  };

  private productCalloutHiddenHandler: (e: CustomEvent) => void = (e: CustomEvent) => {
    if (e?.detail?.manufacturer && e?.detail?.sku) {
      this.selectedProductManufacturer = undefined;
      this.selectedProductSku = undefined;
    }
  };

  private showMessage(message: string) {
    this._snackBar.open(message, 'OK', { duration: 2000 });
  }
}

export interface IHomeConfigShortcut {
  builder: string;
  home: string;
  masterFile: string;
  options: string[];
  themes: string[];
  render: RenderType;
  room: string;
  viewingAngle: number;
  historic: boolean;
  environment: EnvironmentName;
}
export class HomeConfigShortcut implements IHomeConfigShortcut {
  builder: string = '';
  home: string = '';
  masterFile: string = '';
  options: string[] = [];
  themes: string[] = [];
  render: RenderType = RenderType.BestAvailable;
  room: string = '';
  viewingAngle: number = 0;
  historic: boolean = false;
  environment: EnvironmentName = EnvironmentName.Production;
}
class SelectableOption {
  name = '';
  selected = false;

  constructor(name: string, selected: boolean) {
    this.name = name;
    this.selected = selected;
  }
}
class SelectableTheme {
  name = '';
  type = ThemeType.Decorator;
  selected = false;

  constructor(name: string, type: ThemeType, selected: boolean) {
    this.name = name;
    this.type = type;
    this.selected = selected;
  }
}
enum ThemeType {
  Theme,
  Decorator,
  Furnishing,
  FurnishingPlacementScheme,
  ProductReplacement,
  ParameterTheme
}
